import { useMemo, useState } from 'react';
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Loader2,
  Pencil,
  Search,
  Terminal,
  Wrench,
} from 'lucide-react';
import { cn } from '@/utils/cn';

export interface SessionActivityEvent {
  type: 'thinking' | 'search' | 'action' | 'tool_call' | string;
  data?: Record<string, any>;
}

interface SessionActivityProps {
  events: SessionActivityEvent[];
  streaming?: boolean;
}

type ActivityRow = {
  id: string;
  title: string;
  detail?: string;
  status: 'running' | 'done' | 'pending' | 'info';
  kind: SessionActivityEvent['type'];
};

function statusOf(value: unknown): ActivityRow['status'] {
  if (value === 'running') return 'running';
  if (value === 'done') return 'done';
  if (value === 'pending') return 'pending';
  return 'info';
}

function toRows(events: SessionActivityEvent[]): ActivityRow[] {
  const rows: ActivityRow[] = [];
  const upsert = (row: ActivityRow) => {
    const index = rows.findIndex((item) => item.id === row.id);
    if (index >= 0) rows[index] = row;
    else rows.push(row);
  };
  events.forEach((event, eventIndex) => {
    const data = event.data ?? {};
    if (event.type === 'action' && Array.isArray(data.items)) {
      data.items.forEach((item: any, itemIndex: number) => {
        upsert({
          id: `action-${typeof item?.id === 'string' ? item.id : `${eventIndex}-${itemIndex}`}`,
          title: typeof item?.title === 'string' ? item.title : String(item ?? '执行操作'),
          detail: typeof item?.description === 'string' ? item.description : undefined,
          status: statusOf(item?.status),
          kind: event.type,
        });
      });
      return;
    }
    if (event.type === 'thinking') {
      upsert({
        id: `thinking-${typeof data.id === 'string' ? data.id : eventIndex}`,
        title: typeof data.content === 'string' ? data.content : '分析需求与上下文',
        detail: typeof data.duration === 'number' && data.duration > 0 ? `用时 ${data.duration} 秒` : undefined,
        status: statusOf(data.status),
        kind: event.type,
      });
      return;
    }
    if (event.type === 'search') {
      const count = typeof data.count === 'number' ? data.count : 0;
      upsert({
        id: `search-${typeof data.id === 'string' ? data.id : eventIndex}`,
        title: count > 0 ? `检索到 ${count} 条玩法参考` : '检索玩法参考',
        detail: typeof data.query === 'string' ? data.query : undefined,
        status: statusOf(data.status),
        kind: event.type,
      });
      return;
    }
    if (event.type === 'tool_call') {
      upsert({
        id: `tool-${typeof data.jobId === 'string' ? data.jobId : eventIndex}`,
        title: typeof data.toolName === 'string' ? `调用工具：${data.toolName}` : '提交生成任务',
        detail: typeof data.jobId === 'string' ? `任务 ${data.jobId}` : undefined,
        status: 'info',
        kind: event.type,
      });
    }
  });
  return rows;
}

function IconFor({ kind, status }: { kind: string; status: ActivityRow['status'] }) {
  if (status === 'running') return <Loader2 size={14} className="animate-spin text-neon-cyan" />;
  if (status === 'done') return <Check size={14} className="text-neon-green" />;
  if (kind === 'search') return <Search size={14} className="text-neon-blue" />;
  if (kind === 'tool_call') return <Terminal size={14} className="text-neon-purple" />;
  if (kind === 'thinking') return <CircleDot size={14} className="text-neon-cyan" />;
  if (kind === 'action') return <Pencil size={14} className="text-neon-purple" />;
  return <Wrench size={14} className="text-muted-foreground" />;
}

export function SessionActivity({ events, streaming = false }: SessionActivityProps): JSX.Element | null {
  // 过程日志是辅助信息，默认收起，避免流式事件把真正的回答/GDD 推到屏幕下方。
  // 用户主动展开后保留选择，不因每个 SSE 事件更新而闪回收起状态。
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(() => {
    const next = toRows(events);
    // 历史消息可能在旧版本短路分支中把某个 action 永久落成 running。
    // 消息流已经结束后不应继续显示旋转中；新消息仍由实时 streaming 状态驱动。
    return streaming ? next : next.map((row) => row.status === 'running' ? { ...row, status: 'done' as const } : row);
  }, [events, streaming]);
  if (rows.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-border/35 bg-[hsl(var(--card)/0.34)]">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 border-b border-border/25 px-3.5 py-2.5 text-left transition-colors hover:bg-accent/5"
      >
        <span className="text-muted-foreground">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <div className="flex h-5 w-5 items-center justify-center rounded-md bg-neon-cyan/10 text-neon-cyan">
          <BookOpen size={12} />
        </div>
        <span className="flex-1 text-xs font-medium text-foreground/85">会话过程</span>
        <span className="text-[10px] font-mono text-muted-foreground/70">
          {streaming ? '处理中' : `已处理 ${rows.length} 项`}
        </span>
      </button>
      {expanded && (
        <>
          <div className="max-h-72 overflow-y-auto px-3.5 py-2">
            {rows.map((row, index) => (
              <div key={row.id} className="relative flex gap-2.5 py-2 first:pt-1 last:pb-1">
                {index < rows.length - 1 && <span aria-hidden className="absolute left-[6px] top-7 bottom-[-2px] w-px bg-border/45" />}
                <span className={cn(
                  'relative z-10 mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--card))]',
                  row.status === 'running' && 'ring-2 ring-neon-cyan/15',
                )}>
                  <IconFor kind={row.kind} status={row.status} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <p className={cn(
                      'min-w-0 flex-1 text-xs leading-5',
                      row.status === 'running' ? 'text-foreground' : 'text-foreground/75',
                    )}>{row.title}</p>
                    {row.status === 'done' && <span className="shrink-0 text-[10px] text-neon-green/70">完成</span>}
                  </div>
                  {row.detail && <p className="mt-0.5 truncate font-mono text-[10px] leading-4 text-muted-foreground/70" title={row.detail}>{row.detail}</p>}
                </div>
              </div>
            ))}
          </div>
          {streaming && (
            <div className="flex items-center gap-1.5 border-t border-border/20 px-3.5 py-2 text-[10px] text-muted-foreground/70">
              <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-neon-cyan" />
              正在等待下一步交互
            </div>
          )}
        </>
      )}
    </div>
  );
}
